package org.teddapp.project.ui.mindmap

import kotlin.math.max

/**
 * Disposition d'une carte heuristique : où va chaque nœud, sans rien dessiner.
 *
 * Fonction pure, sans Compose : testable, et lancée hors du fil d'interface.
 * L'arbre est posé autour de sa racine ; les branches de niveau 1 se répartissent
 * à droite et à gauche pour équilibrer les hauteurs (mode [LayoutMode.Radial]), ou
 * toutes à droite sur un écran étroit ([LayoutMode.LeftToRight]). Chaque sous-arbre
 * occupe une bande verticale égale à la somme de celles de ses enfants ; le nœud
 * se centre dans la sienne. Un nœud replié ne montre pas ses enfants.
 */
class LayoutNode(
    val id: String,
    val parentId: String?,
    val order: Long,
    val text: String,
    val createdAt: Long = 0L,
)

/** Taille d'une boîte de nœud, marges comprises, en pixels monde. */
class LayoutSize(val width: Float, val height: Float)

class LayoutRect(val left: Float, val top: Float, val width: Float, val height: Float) {
    val right: Float get() = left + width
    val bottom: Float get() = top + height
    val centerX: Float get() = left + width / 2f
    val centerY: Float get() = top + height / 2f

    fun contains(x: Float, y: Float): Boolean = x >= left && x <= right && y >= top && y <= bottom

    fun intersects(other: LayoutRect): Boolean =
        left <= other.right && right >= other.left && top <= other.bottom && bottom >= other.top

    companion object {
        val Empty = LayoutRect(0f, 0f, 0f, 0f)
    }
}

enum class LayoutMode { Radial, LeftToRight }

enum class LayoutSide { Left, Right }

class LayoutEdge(val parentId: String, val childId: String, val side: LayoutSide, val depth: Int, val branch: Int)

/**
 * Un nœud placé. [branch] : index de la branche de niveau 1 dont il descend (−1 pour
 * la racine), ce qui donne sa teinte. [hiddenDescendants] : ce que cache un nœud replié.
 */
class PlacedNode(
    val node: LayoutNode,
    val rect: LayoutRect,
    val depth: Int,
    val side: LayoutSide,
    val branch: Int,
    val hiddenDescendants: Int,
) {
    val id: String get() = node.id
}

class MindMapLayoutResult(
    /** Les nœuds visibles, triés par bord haut : la recherche du visible se fait par dichotomie. */
    val nodes: List<PlacedNode>,
    val byId: Map<String, PlacedNode>,
    val edges: List<LayoutEdge>,
    val bounds: LayoutRect,
    val rootId: String?,
    /** Enfants visibles, dans l'ordre, et parent de chaque nœud visible. */
    val childrenOf: Map<String, List<String>>,
    val parentOf: Map<String, String>,
) {
    /** Le nœud sous un point, ou null. */
    fun hit(x: Float, y: Float): PlacedNode? {
        // Les nœuds sont triés par bord haut : on part du premier qui peut contenir y.
        var lo = 0
        var hi = nodes.size
        while (lo < hi) {
            val mid = (lo + hi) ushr 1
            if (nodes[mid].rect.top <= y) lo = mid + 1 else hi = mid
        }
        for (i in lo - 1 downTo 0) {
            val n = nodes[i]
            if (n.rect.contains(x, y)) return n
            // Les boîtes ont une hauteur bornée : au-delà, plus rien ne peut contenir y.
            if (y - n.rect.top > MAX_BOX_HEIGHT) break
        }
        return null
    }

    /** Indices [from, to) des nœuds dont la bande verticale touche [top, bottom]. */
    fun visibleRange(top: Float, bottom: Float): IntRange {
        var lo = 0
        var hi = nodes.size
        while (lo < hi) {
            val mid = (lo + hi) ushr 1
            if (nodes[mid].rect.top < top - MAX_BOX_HEIGHT) lo = mid + 1 else hi = mid
        }
        val start = lo
        hi = nodes.size
        while (lo < hi) {
            val mid = (lo + hi) ushr 1
            if (nodes[mid].rect.top <= bottom) lo = mid + 1 else hi = mid
        }
        return start until lo
    }

    /** Le chemin de la racine au nœud, racine en premier. */
    fun pathTo(id: String): List<String> {
        val out = ArrayDeque<String>()
        var current: String? = id
        while (current != null) {
            out.addFirst(current)
            current = parentOf[current]
        }
        return out.toList()
    }

    companion object {
        /** Hauteur maximale d'une boîte : borne la recherche par dichotomie. */
        const val MAX_BOX_HEIGHT = 400f
        val Empty = MindMapLayoutResult(emptyList(), emptyMap(), emptyList(), LayoutRect.Empty, null, emptyMap(), emptyMap())
    }
}

/**
 * Calcule les dispositions et garde en mémoire ce qui ne change pas : les tailles
 * mesurées (par nœud et texte) et les hauteurs de sous-arbres. Renommer ou plier un
 * nœud n'invalide que son chemin vers la racine, pas toute la carte. Le placement
 * lui-même est linéaire et refait à chaque appel : c'est la mesure du texte qui coûte.
 */
class MindMapLayoutEngine(
    private val measure: (LayoutNode) -> LayoutSize,
    val horizontalGap: Float = H_GAP,
    val verticalGap: Float = V_GAP,
) {
    private class Measured(val text: String, val size: LayoutSize)

    private val sizes = HashMap<String, Measured>()
    private val heights = HashMap<String, Float>()
    private var lastNodes: Map<String, LayoutNode> = emptyMap()
    private var lastCollapsed: Set<String> = emptySet()

    /** Dispose [nodes] ; [collapsed] sont les nœuds dont on cache les enfants. */
    fun layout(nodes: List<LayoutNode>, collapsed: Set<String>, mode: LayoutMode): MindMapLayoutResult {
        if (nodes.isEmpty()) {
            lastNodes = emptyMap(); lastCollapsed = emptySet(); heights.clear(); sizes.clear()
            return MindMapLayoutResult.Empty
        }
        val byId = HashMap<String, LayoutNode>(nodes.size * 2)
        for (n in nodes) byId[n.id] = n
        invalidate(byId, collapsed)

        // Enfants dans l'ordre. Plusieurs racines (fusion hors-ligne) : la plus ancienne
        // garde la place, les autres deviennent ses enfants à l'affichage.
        val children = HashMap<String, MutableList<LayoutNode>>()
        val roots = ArrayList<LayoutNode>()
        for (n in nodes) {
            val parent = n.parentId?.takeIf { it in byId && it != n.id }
            if (parent == null) roots += n else children.getOrPut(parent) { ArrayList() } += n
        }
        roots.sortWith(compareBy<LayoutNode> { it.createdAt }.thenBy { it.id })
        val root = roots.first()
        if (roots.size > 1) children.getOrPut(root.id) { ArrayList() }.addAll(roots.drop(1))
        for (list in children.values) list.sortWith(compareBy<LayoutNode> { it.order }.thenBy { it.createdAt }.thenBy { it.id })
        // Un cycle laissé par une fusion rendrait la descente infinie : on coupe les
        // enfants qui sont aussi des ancêtres de la racine (jamais atteints) en ne
        // visitant chaque nœud qu'une fois.
        val visited = HashSet<String>(nodes.size * 2)

        val placed = ArrayList<PlacedNode>(nodes.size)
        val edges = ArrayList<LayoutEdge>(nodes.size)
        val childrenOf = HashMap<String, List<String>>()
        val parentOf = HashMap<String, String>()

        fun size(n: LayoutNode): LayoutSize = measured(n)

        fun height(n: LayoutNode): Float {
            heights[n.id]?.let { return it }
            val own = size(n).height
            val kids = if (n.id in collapsed) null else children[n.id]
            val h = if (kids.isNullOrEmpty()) own else {
                var sum = 0f
                for (c in kids) sum += height(c)
                max(own, sum + verticalGap * (kids.size - 1))
            }
            heights[n.id] = h
            return h
        }

        fun descendants(n: LayoutNode): Int {
            var count = 0
            val stack = ArrayDeque<LayoutNode>()
            stack.addLast(n)
            while (stack.isNotEmpty()) {
                val k = children[stack.removeLast().id] ?: continue
                count += k.size
                for (c in k) stack.addLast(c)
            }
            return count
        }

        fun place(n: LayoutNode, side: LayoutSide, x: Float, centerY: Float, depth: Int, branch: Int) {
            if (!visited.add(n.id)) return
            val s = size(n)
            val left = if (side == LayoutSide.Right) x else x - s.width
            val rect = LayoutRect(left, centerY - s.height / 2f, s.width, s.height)
            val folded = n.id in collapsed && !children[n.id].isNullOrEmpty()
            placed += PlacedNode(n, rect, depth, side, branch, if (folded) descendants(n) else 0)
            val kids = if (folded) null else children[n.id]
            if (kids.isNullOrEmpty()) { childrenOf[n.id] = emptyList(); return }
            childrenOf[n.id] = kids.map { it.id }
            var total = -verticalGap
            for (c in kids) total += height(c) + verticalGap
            var cursor = centerY - total / 2f
            val childX = if (side == LayoutSide.Right) rect.right + horizontalGap else rect.left - horizontalGap
            for ((i, c) in kids.withIndex()) {
                val h = height(c)
                parentOf[c.id] = n.id
                edges += LayoutEdge(n.id, c.id, side, depth + 1, if (depth == 0) i else branch)
                place(c, side, childX, cursor + h / 2f, depth + 1, if (depth == 0) i else branch)
                cursor += h + verticalGap
            }
        }

        // La racine, puis ses branches réparties par côté.
        val rootSize = size(root)
        val rootRect = LayoutRect(-rootSize.width / 2f, -rootSize.height / 2f, rootSize.width, rootSize.height)
        visited.add(root.id)
        val level1 = if (root.id in collapsed) emptyList() else children[root.id].orEmpty()
        placed += PlacedNode(root, rootRect, 0, LayoutSide.Right, -1, if (root.id in collapsed) descendants(root) else 0)
        childrenOf[root.id] = level1.map { it.id }

        val right = ArrayList<LayoutNode>()
        val leftSide = ArrayList<LayoutNode>()
        if (mode == LayoutMode.LeftToRight) right.addAll(level1) else {
            var hr = 0f
            var hl = 0f
            for (c in level1) {
                val h = height(c)
                // Glouton : chaque branche va du côté le moins haut, l'ordre reste lisible.
                if (hr <= hl) { right += c; hr += h + verticalGap } else { leftSide += c; hl += h + verticalGap }
            }
        }
        fun stack(branches: List<LayoutNode>, side: LayoutSide) {
            if (branches.isEmpty()) return
            var total = -verticalGap
            for (c in branches) total += height(c) + verticalGap
            var cursor = rootRect.centerY - total / 2f
            val x = if (side == LayoutSide.Right) rootRect.right + horizontalGap else rootRect.left - horizontalGap
            for (c in branches) {
                val h = height(c)
                val branch = level1.indexOf(c)
                parentOf[c.id] = root.id
                edges += LayoutEdge(root.id, c.id, side, 1, branch)
                place(c, side, x, cursor + h / 2f, 1, branch)
                cursor += h + verticalGap
            }
        }
        stack(right, LayoutSide.Right)
        stack(leftSide, LayoutSide.Left)

        placed.sortBy { it.rect.top }
        var l = Float.POSITIVE_INFINITY; var t = Float.POSITIVE_INFINITY
        var r = Float.NEGATIVE_INFINITY; var b = Float.NEGATIVE_INFINITY
        for (p in placed) {
            if (p.rect.left < l) l = p.rect.left
            if (p.rect.top < t) t = p.rect.top
            if (p.rect.right > r) r = p.rect.right
            if (p.rect.bottom > b) b = p.rect.bottom
        }
        val index = HashMap<String, PlacedNode>(placed.size * 2)
        for (p in placed) index[p.id] = p
        lastNodes = byId
        lastCollapsed = collapsed
        return MindMapLayoutResult(placed, index, edges, LayoutRect(l, t, r - l, b - t), root.id, childrenOf, parentOf)
    }

    /** Oublie les hauteurs des nœuds touchés et de leurs ancêtres ; la mesure d'un texte inchangé reste. */
    private fun invalidate(nodes: Map<String, LayoutNode>, collapsed: Set<String>) {
        if (lastNodes.isEmpty()) { heights.clear(); return }
        val dirty = HashSet<String>()
        for ((id, n) in nodes) {
            val old = lastNodes[id]
            if (old == null || old.text != n.text || old.parentId != n.parentId || old.order != n.order) {
                dirty += id
                old?.parentId?.let { dirty += it }
                n.parentId?.let { dirty += it }
            }
        }
        for (id in lastNodes.keys) if (id !in nodes) { dirty += id; lastNodes[id]?.parentId?.let { dirty += it } }
        for (id in collapsed) if (id !in lastCollapsed) dirty += id
        for (id in lastCollapsed) if (id !in collapsed) dirty += id
        if (dirty.isEmpty()) return
        // Remonter jusqu'à la racine depuis chaque nœud touché.
        val stack = ArrayDeque(dirty)
        val seen = HashSet<String>()
        while (stack.isNotEmpty()) {
            val id = stack.removeLast()
            if (!seen.add(id)) continue
            heights.remove(id)
            (nodes[id] ?: lastNodes[id])?.parentId?.let { stack.addLast(it) }
        }
        sizes.keys.retainAll(nodes.keys)
    }

    private fun measured(n: LayoutNode): LayoutSize {
        val cached = sizes[n.id]
        if (cached != null && cached.text == n.text) return cached.size
        val s = measure(n)
        sizes[n.id] = Measured(n.text, s)
        return s
    }

    companion object {
        const val H_GAP = 56f
        const val V_GAP = 12f
    }
}
